// WebP container reader (RIFF). The browser decodes the pixels (createImageBitmap); this only reads
// what the browser does not tell us: is it animated, does it have alpha, is it lossless, its stored
// size, and the EXIF and ICC chunks. Spec: https://developers.google.com/speed/webp/docs/riff_container
//
//   "RIFF" <u32 size> "WEBP", then chunks: <fourcc> <u32 LE size> <payload> (+1 pad byte if odd).
//   Simple files hold one "VP8 " (lossy) or "VP8L" (lossless) chunk. Extended files start with "VP8X"
//   (flags: 0x20 ICC, 0x10 alpha, 0x08 EXIF, 0x04 XMP, 0x02 animation; canvas width-1 / height-1 as
//   24-bit LE) and may hold ICCP, ANIM + ANMF frames, ALPH, EXIF and XMP chunks.

export type WebpInfo = {
  width: number;
  height: number;
  animated: boolean;
  alpha: boolean;
  lossless: boolean;
  /** The EXIF chunk as a TIFF block ("II*\0" / "MM\0*"), with any "Exif\0\0" prefix removed. */
  exif: Uint8Array | null;
  icc: Uint8Array | null;
};

const fourcc = (b: Uint8Array, o: number) => String.fromCharCode(b[o]!, b[o + 1]!, b[o + 2]!, b[o + 3]!);
const u24 = (b: Uint8Array, o: number) => b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16);
const u32 = (b: Uint8Array, o: number) =>
  (b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16) | (b[o + 3]! << 24)) >>> 0;

/** True for a RIFF / WEBP file header. */
export function isWebp(b: Uint8Array): boolean {
  return b.length >= 12 && fourcc(b, 0) === 'RIFF' && fourcc(b, 8) === 'WEBP';
}

/** The container facts of a WebP file, or null when it is not a readable WebP. */
export function readWebp(b: Uint8Array): WebpInfo | null {
  if (!isWebp(b)) return null;
  const info: WebpInfo = {
    width: 0,
    height: 0,
    animated: false,
    alpha: false,
    lossless: false,
    exif: null,
    icc: null,
  };
  let haveSize = false;
  let o = 12;
  while (o + 8 <= b.length) {
    const id = fourcc(b, o);
    const size = u32(b, o + 4);
    const p = o + 8;
    if (p + size > b.length) break; // truncated: keep what was read
    switch (id) {
      case 'VP8X':
        if (size >= 10) {
          const flags = b[p]!;
          info.animated ||= (flags & 0x02) !== 0;
          info.alpha ||= (flags & 0x10) !== 0;
          info.width = u24(b, p + 4) + 1;
          info.height = u24(b, p + 7) + 1;
          haveSize = true;
        }
        break;
      case 'ANIM':
      case 'ANMF':
        info.animated = true;
        break;
      case 'ALPH':
        info.alpha = true;
        break;
      case 'VP8L':
        info.lossless = true;
        // 0x2f signature, then 14 bits width-1, 14 bits height-1, 1 bit alpha_is_used.
        if (size >= 5 && b[p] === 0x2f) {
          const bits = u32(b, p + 1);
          if (!haveSize) {
            info.width = (bits & 0x3fff) + 1;
            info.height = ((bits >>> 14) & 0x3fff) + 1;
            haveSize = true;
          }
          if ((bits >>> 28) & 1) info.alpha = true;
        }
        break;
      case 'VP8 ':
        // Frame tag (3 bytes), start code 9d 01 2a, then 14-bit width and height (2 scale bits each).
        if (!haveSize && size >= 10 && b[p + 3] === 0x9d && b[p + 4] === 0x01 && b[p + 5] === 0x2a) {
          info.width = (b[p + 6]! | (b[p + 7]! << 8)) & 0x3fff;
          info.height = (b[p + 8]! | (b[p + 9]! << 8)) & 0x3fff;
          haveSize = true;
        }
        break;
      case 'EXIF': {
        let e = b.subarray(p, p + size);
        if (e.length >= 6 && fourcc(e, 0) === 'Exif' && e[4] === 0 && e[5] === 0) e = e.subarray(6);
        info.exif = e.length ? e : null;
        break;
      }
      case 'ICCP':
        info.icc = b.subarray(p, p + size);
        break;
    }
    o = p + size + (size & 1);
  }
  return haveSize ? info : null;
}
